/**
 * Realtime feature configuration.
 *
 * Every value is BUILD-TIME: Vite inlines `import.meta.env` when the bundle is
 * built, so changing one requires a rebuild (`npm run build`). It reads only
 * from `.env.production` — the server `.env` is not visible to the browser.
 *
 * The former hardware/ui/performance/debug flag families were removed: nothing
 * in the app read them, so setting e.g. VITE_HARDWARE_RECEIPT_PRINTER=false had
 * no effect at all — an operator trap rather than a feature switch.
 */

interface WebSocketConfig {
  host: string;
  port: number;
  secure: boolean;
  key: string;
}

interface RealtimeFeatures {
  enabled: boolean;
  websocket: WebSocketConfig;
}

// An https page cannot open a ws:// socket (mixed content), so TLS is implied
// by the page protocol as well as by an explicit VITE_REVERB_SCHEME=https.
// Guard `window`: this module is also imported by Node-side tooling/tests
// without a DOM, where a bare `window` access throws at evaluation time.
const isBrowser = typeof window !== 'undefined'
const isSecureWebsocket =
  import.meta.env.VITE_REVERB_SCHEME === 'https' || (isBrowser && window.location.protocol === 'https:')

export const REALTIME_FEATURES: RealtimeFeatures = {
  enabled: import.meta.env.VITE_REALTIME_ENABLED !== 'false',
  websocket: {
    // VITE_REVERB_* only. The legacy VITE_WEBSOCKET_* surface was removed: a
    // stale port there (6001) pointed every browser at a host where nothing
    // listens, and its silent fallback hid a missing Reverb configuration.
    // An empty key/host disables realtime — echoClient.ts reports that loudly.
    // In dev mode fallback to hostname for convenience; in production keep empty to trigger missing-config alert.
    host: import.meta.env.VITE_REVERB_HOST || (import.meta.env.DEV && isBrowser ? window.location.hostname : ''),
    // Default by scheme, never 8080: that is Reverb's internal bind port and
    // is unreachable from a browser behind the Nginx wss proxy.
    port: parseInt(import.meta.env.VITE_REVERB_PORT, 10) || (isSecureWebsocket ? 443 : 80),
    secure: isSecureWebsocket,
    key: import.meta.env.VITE_REVERB_APP_KEY || '',
  },
}
